import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, ChevronRight } from 'lucide-react';
import RatingFlow from './RatingFlow';
import { parseLocalDate, formatDate } from '../utils/timeFormat';

const PendingRatingsWidget = ({ userRole }) => {
  const [pendingRatings, setPendingRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showRatingDialog, setShowRatingDialog] = useState(false);

  useEffect(() => {
    fetchPendingRatings();
  }, []);

  const fetchPendingRatings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/performance/ratings/pending');
      setPendingRatings(response.data || []);
    } catch (error) {
      console.error('Failed to load pending ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRateClick = (booking) => {
    setSelectedBooking(booking);
    setShowRatingDialog(true);
  };

  const handleRatingSubmitted = () => {
    fetchPendingRatings();
  };

  if (loading) {
    return null; // Don't show loading state for widget
  }

  if (pendingRatings.length === 0) {
    return null; // Don't show widget if no pending ratings
  }

  const ratingType = userRole === 'talent' ? 'talent_to_client' : 'client_to_talent';

  return (
    <>
      <Card className="bg-white border-amber-200 shadow-sm" data-testid="pending-ratings-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Rate Your Recent Gigs
            <Badge className="bg-amber-100 text-amber-700 ml-auto">
              {pendingRatings.length} pending
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Help improve the platform by rating your recent experiences
          </p>
          <div className="space-y-2">
            {pendingRatings.slice(0, 3).map((booking) => (
              <div
                key={booking.booking_id}
                className="flex items-center justify-between p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors cursor-pointer"
                onClick={() => handleRateClick(booking)}
                data-testid="pending-rating-item"
              >
                <div>
                  <p className="font-medium text-sm">{booking.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {booking.role_name} • {booking.venue_name}
                  </p>
                  {booking.shift_date && (
                    <p className="text-xs text-muted-foreground">
                      {booking.shift_date ? formatDate(booking.shift_date) : ''}
                    </p>
                  )}
                </div>
                <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-700">
                  Rate <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
          {pendingRatings.length > 3 && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              +{pendingRatings.length - 3} more to rate
            </p>
          )}
        </CardContent>
      </Card>

      {/* Rating Dialog */}
      <RatingFlow
        isOpen={showRatingDialog}
        onClose={() => setShowRatingDialog(false)}
        booking={selectedBooking}
        ratingType={ratingType}
        onRatingSubmitted={handleRatingSubmitted}
      />
    </>
  );
};

export default PendingRatingsWidget;
